import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { RadioStation } from '@/mocks/radios';
import { radioStations } from '@/mocks/radios';
import { getRadioCommunity, getSimilarStations, type RadioCommunity, type CommunityActivity } from '@/mocks/radioCommunity';

interface SeccionComunidadProps {
  station: RadioStation;
}

const ACTIVITY_ICONS: Record<CommunityActivity['type'], { icon: string; color: string; bg: string }> = {
  follow: { icon: 'ri-user-add-line', color: 'text-primary-600', bg: 'bg-primary-100' },
  reaction: { icon: 'ri-heart-3-line', color: 'text-red-500', bg: 'bg-red-100' },
  share: { icon: 'ri-share-forward-line', color: 'text-accent-600', bg: 'bg-accent-100' },
  favorite: { icon: 'ri-star-fill', color: 'text-amber-500', bg: 'bg-amber-100' },
};

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function StatCard({ icon, label, value, accentClass }: { icon: string; label: string; value: string; accentClass: string }) {
  return (
    <div className="flex items-center gap-4 px-5 py-4 bg-background-50 rounded-2xl border border-background-200/70">
      <div className={`w-12 h-12 rounded-xl ${accentClass} flex items-center justify-center shrink-0`}>
        <i className={`${icon} text-xl`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wider">{label}</p>
        <p className="text-xl font-bold text-foreground-950">{value}</p>
      </div>
    </div>
  );
}

function ActivityItem({ activity }: { activity: CommunityActivity }) {
  const config = ACTIVITY_ICONS[activity.type];
  return (
    <div className="flex items-start gap-3 py-3 px-4 rounded-xl hover:bg-background-100/80 transition-colors">
      <div className="relative shrink-0">
        <img
          src={activity.avatarUrl}
          alt={activity.userName}
          className="w-9 h-9 rounded-full object-cover border-2 border-background-200"
          loading="lazy"
        />
        <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full ${config.bg} flex items-center justify-center border-2 border-background-50`}>
          <i className={`${config.icon} text-[10px] ${config.color}`}></i>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] text-foreground-800">
          <span className="font-semibold text-foreground-950">{activity.userName}</span>{' '}
          {activity.detail}
        </p>
        <span className="text-[11px] text-foreground-400">{activity.timestamp}</span>
      </div>
    </div>
  );
}

export default function SeccionComunidad({ station }: SeccionComunidadProps) {
  const community = getRadioCommunity(station.id);
  const [isFollowing, setIsFollowing] = useState(community?.isFollowing ?? false);
  const [showCopyToast, setShowCopyToast] = useState(false);

  if (!community) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-group-line text-3xl text-foreground-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-950 mb-2">Comunidad no disponible</h3>
          <p className="text-foreground-500 text-sm">Esta estación no tiene datos de comunidad en este momento.</p>
        </div>
      </div>
    );
  }

  const similarIds = getSimilarStations(station.id);
  const similarStations = radioStations.filter((s) => similarIds.includes(s.id) && s.id !== station.id);

  const handleFollowToggle = () => {
    setIsFollowing((prev) => !prev);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    try {
      await navigator.clipboard.writeText(url);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2500);
    } catch {
      // fallback silently
    }
  };

  return (
    <div className="space-y-10 relative">
      {/* ── TOAST ── */}
      {showCopyToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-foreground-900 text-background-50 text-sm font-medium rounded-xl shadow-lg flex items-center gap-2 animate-bounce">
          <i className="ri-check-line text-green-400"></i>
          Enlace copiado al portapapeles
        </div>
      )}

      {/* ── ESTADÍSTICAS ── */}
      <section>
        <h3 className="text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
          <i className="ri-bar-chart-2-fill text-primary-500"></i>
          Estadísticas de Comunidad
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon="ri-user-heart-line"
            label="Seguidores"
            value={formatNumber(community.followers)}
            accentClass="bg-primary-100 text-primary-600"
          />
          <StatCard
            icon="ri-star-line"
            label="Radios Favoritas"
            value={formatNumber(community.favoritesCount)}
            accentClass="bg-amber-100 text-amber-600"
          />
          <StatCard
            icon="ri-user-follow-line"
            label="Radios Seguidas"
            value={community.followingCount.toString()}
            accentClass="bg-accent-100 text-accent-600"
          />
          <StatCard
            icon="ri-heart-pulse-line"
            label="Reacciones"
            value={formatNumber(community.totalReactions)}
            accentClass="bg-red-100 text-red-500"
          />
        </div>
      </section>

      {/* ── ACCIONES ── */}
      <section>
        <h3 className="text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
          <i className="ri-flashlight-fill text-accent-500"></i>
          Acciones
        </h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleFollowToggle}
            className={`flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              isFollowing
                ? 'bg-background-200/60 text-foreground-600 hover:bg-red-50 hover:text-red-600 border border-background-200/70'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600 shadow-sm'
            }`}
          >
            {isFollowing ? (
              <>
                <i className="ri-user-unfollow-line text-lg"></i>
                Dejar de Seguir
              </>
            ) : (
              <>
                <i className="ri-user-follow-line text-lg"></i>
                Seguir Radio
              </>
            )}
          </button>

          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl bg-background-50 border border-background-200/70 text-foreground-700 text-sm font-bold hover:bg-background-100 hover:border-secondary-300 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-share-forward-line text-lg"></i>
            Compartir Radio
          </button>

          <span className="flex items-center text-sm text-foreground-500 px-3">
            <i className="ri-information-line mr-1.5"></i>
            {isFollowing ? 'Siguiendo esta estación' : 'Únete a la comunidad'}
          </span>
        </div>
      </section>

      {/* ── ACTIVIDAD RECIENTE ── */}
      <section>
        <h3 className="text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
          <i className="ri-history-fill text-secondary-500"></i>
          Actividad Reciente
        </h3>
        <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden divide-y divide-background-200/50">
          {community.recentActivity.map((activity) => (
            <ActivityItem key={activity.id} activity={activity} />
          ))}
        </div>
        <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-background-50 border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition-all cursor-pointer">
          <i className="ri-more-line"></i>
          Ver más actividad
        </button>
      </section>

      {/* ── RADIOS SIMILARES ── */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-foreground-950 flex items-center gap-2">
            <i className="ri-stack-line text-accent-500"></i>
            Radios Similares
          </h3>
          <Link
            to="/"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap"
          >
            Ver todas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {similarStations.length === 0 ? (
          <div className="py-10 text-center bg-background-50 rounded-2xl border border-background-200/70">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-signal-tower-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">No se encontraron radios similares.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {similarStations.map((s) => {
              const freqSlug = s.frequency.replace('.', '-');
              return (
                <Link
                  key={s.id}
                  to={`/radio/${s.slug || freqSlug}`}
                  className="flex items-center gap-4 p-4 bg-background-50 border border-background-200/70 rounded-2xl hover:border-primary-300 hover:bg-background-100/80 transition-all cursor-pointer group"
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-foreground-200 shrink-0">
                    <img src={s.logoUrl} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors truncate">
                      {s.name}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] font-semibold text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                        {s.frequency}
                      </span>
                      <span className="text-[11px] text-foreground-500">{s.category}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-[10px]"></i>
                        {s.city}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line text-[10px]"></i>
                        {formatNumber(s.listeners)}
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 text-foreground-300 group-hover:text-primary-500 transition-colors">
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}